// React
import React from 'react';

// Packages
import cc from 'classcat';

// Utilities
import { useLabels, useActivityTagging } from 'utilities/hooks';
import { useInitiativeDataStore } from 'utilities/store';
import { CONSTANTS } from 'utilities/configuration';

// Components
import {
    ActivityMetrics,
    ActivityReflections,
    ActivityDetails,
    ActivityChoice,
    ActivityDataCard,
    ActivitySetFieldValues,
} from '../_common';
import { ActivityPhysicalDetails } from 'components/_cardContents/activityPhysical/activityPhysical';

const Name = CONSTANTS.ACTIVITIES.PHYSICAL;

const Choice = props => {
    return ActivityChoice({
        Name,
        methods: 'Initiative_Activity__c.Implementation_Stage__c',
        fieldName: 'Implementation_Stage__c',
        ...props,
    });
};

const DataCard = ({ item, theme = 'teal', mode = [] }) => {
    return (
        <ActivityDataCard
            {...{
                item,
                theme,
                methodsFieldName: 'Implementation_Stage__c',
                tagTypes: [CONSTANTS.TAGS.PHYSICAL_ACTIVITY],
                showTags: mode.includes('tags'),
                showDetails: mode.includes('details'),
            }}>
            <ActivityPhysicalDetails {...{ item, theme }} />
        </ActivityDataCard>
    );
};

const Details = props => {
    // ///////////////////
    // STORES
    // ///////////////////

    const { utilities } = useInitiativeDataStore();

    // ///////////////////
    // HOOKS
    // ///////////////////

    const { object, dataSet, label } = useLabels();
    const tagging = useActivityTagging({
        types: [CONSTANTS.TAGS.PHYSICAL_ACTIVITY],
    });

    // ///////////////////
    // METHODS
    // ///////////////////

    function getItemData(formData) {
        const {
            Value,
            Construction_Start_Date__c,
            Construction_End_Date__c,
            Regulations,
            Implementation_Stage__c,
        } = formData;

        // Data for sf
        return {
            Implementation_Stage__c,

            Financial_Value__c: Value[0]?.textValue,
            CurrencyIsoCode: Value[0]?.selectValue,

            Construction_Start_Date__c,
            Construction_End_Date__c,

            regulations: Regulations.map(item => item.selectValue),
        };
    }

    function addItemCallback(data) {
        const { _regulations } = data;

        utilities.updateInitiativeDataRelations('_regulations', _regulations);
    }

    function editItemCallback(data) {
        const { Id, _regulations } = data;

        utilities.removeInitiativeDataRelations(
            '_regulations',
            item => item.Initiative_Activity__c === Id
        );

        utilities.updateInitiativeDataRelations('_regulations', _regulations);
    }

    // ///////////////////
    // FIELDS
    // ///////////////////

    function fields() {
        // Regulations
        const regulations = utilities.regulations.getAll();

        return [
            {
                type: 'DatePicker',
                name: 'Construction_Start_Date__c',
                object: object.config(
                    'Initiative_Activity__c.Construction_Start_Date__c'
                ),
            },
            {
                type: 'DatePicker',
                name: 'Construction_End_Date__c',
                object: object.config(
                    'Initiative_Activity__c.Construction_End_Date__c'
                ),
            },
            {
                type: 'SelectList',
                name: 'Value',
                object: object.config(
                    'Initiative_Activity__c.Financial_Value__c'
                ),
                selectLabel: object.label(
                    'Initiative_Activity__c.CurrencyIsoCode'
                ),
                textLabel: object.label(
                    'Initiative_Activity__c.Financial_Value__c__Amount'
                ),
                options: dataSet('Currencies'),
                showText: true,
                listMaxLength: 1,
            },
            {
                type: 'SelectList',
                name: 'Regulations',
                object: object.config('Initiative_Activity__c.Regulations'),
                options: regulations.map(regulation => ({
                    value: regulation.Id,
                    label: regulation.Id__c,
                    optionDisabled: regulation.Initiative_Activity__c !== null,
                })),
                missingOptionsLabel: label('EmptyStateInputRegulations'),
            },
        ];
    }

    // ///////////////////
    // RETURN
    // ///////////////////

    return ActivityDetails({
        addItemCallback,
        editItemCallback,
        tagging: tagging.activity,
        fields,
        getItemData,
        ...props,
    });
};

const Metrics = props => {
    return ActivityMetrics({
        Name,
        ...props,
    });
};

const Reflections = props => {
    return ActivityReflections({
        ...props,
    });
};

const setFieldValues = ({
    tagging,
    choice,
    form,
    id,
    utilities,
    REPORT_ID,
}) => {
    // ///////////////////
    // DETAILS
    // ///////////////////

    const {
        Implementation_Stage__c,
        Construction_Start_Date__c,
        Construction_End_Date__c,
        CurrencyIsoCode,
        Financial_Value__c,
    } = utilities.activities.get(id);

    // Choice
    form.setValue('Implementation_Stage__c', Implementation_Stage__c);

    // Dates data
    form.setValue('Construction_Start_Date__c', Construction_Start_Date__c);
    form.setValue('Construction_End_Date__c', Construction_End_Date__c);

    // Value field
    form.setValue('Value', [
        {
            selectValue: CurrencyIsoCode,
            textValue: Financial_Value__c,
        },
    ]);

    // Value data for view usage
    form.setValue('CurrencyIsoCode', CurrencyIsoCode);
    form.setValue('Financial_Value__c', Financial_Value__c);

    // Regulations
    form.setValue(
        'Regulations',
        utilities.regulations.getFromActivityId(id).map(regulation => ({
            selectValue: regulation.Id,
        }))
    );

    // ///////////////////
    // ALL THE OTHER STUFF
    // ///////////////////

    ActivitySetFieldValues({
        REPORT_ID,
        tagging,
        choice,
        form,
        id,
        utilities,
    });
};

export {
    Name,
    DataCard,
    Choice,
    Details,
    Metrics,
    Reflections,
    setFieldValues,
};
